import './contact-info.component.ajscss';

import { IOutgoingMailDeprecatedService } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { IUnitOutgoingCallDeprecatedService } from '@agent/core/phone/outcall/unit-outgoing-call-deprecated.service';
import { ICustomerCardsDeprecatedService } from '@commons/customer/customer-cards-deprecated.service';
import { IEpticaExctractAttachment } from '@commons/history/history-mail/models/eptica-extract';
import { ChannelUtil } from '@commons/util/helper/channel';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions } from 'angular';
import { i18n } from 'i18next';
import { combineLatest, filter } from 'rxjs';

import {
  Booking,
  BookingFacade,
  capitalize,
  ChanEnum,
  Channel,
  ChannelType,
  Civility,
  Customer,
  CustomerFacade,
  isNotNullOrUndefined,
  MediaType,
  MomentService,
  Option,
} from '@contact/upgrade';

import { UserUtil } from '@contact/common-util-types';

import templateUrl from './contact-info.component.ajs.html';

const MODULE_NAME = 'contactContactInfo';

export class ContactInfoCtrl implements IComponentController {
  public name = MODULE_NAME;
  MediaType = MediaType;
  Civility = Civility;

  contact$ = this.contactFacade.selected$;
  customer$ = this.contactFacade.selectedCustomer$;

  contact: Booking | undefined;
  customer: Customer | undefined;
  unknownContact: boolean = true;
  isDummy: boolean = false;
  contactName: string = '';
  contactCompanyName: string = '';
  contactCivility: Civility = Civility.NOTSET;
  contactSkills: string[] = [];
  contactTransferred: boolean = false;
  contactTransferredByABot: boolean = false;
  transferredMessage: string = '';
  transferRequestingCsrName: string = '';
  transferOldSkillNames: string[] = [];
  contactRescheduled: boolean = false;
  isUnitOutgoingCallFakeContact: boolean = false;
  isUnitOutgoingMailFakeContact: boolean = false;
  isSocialNetworkFakeContact: boolean = false;
  contactRescheduledDate: Date | undefined;
  contactRescheduledDescription: string | undefined;

  customers: Customer[] = [];
  selectCustomer: Customer | undefined;
  hideHelpIcon: boolean = false;

  transferredAttachments: IEpticaExctractAttachment[];

  constructor(
    private readonly $i18next: i18n,
    private readonly customerCardsDeprecatedService: ICustomerCardsDeprecatedService,
    private readonly unitOutgoingCallDeprecatedService: IUnitOutgoingCallDeprecatedService,
    private readonly outgoingMailDeprecatedService: IOutgoingMailDeprecatedService,
    private readonly momentService: MomentService,
    private readonly contactFacade: BookingFacade,
    private readonly customerFacade: CustomerFacade
  ) {}

  $onInit(): void {
    // compute all propretes from contact/customer informations
    combineLatest(this.contact$, this.customer$)
      .pipe(ng1Destroy(this))
      .subscribe(([contact, customer]) => {
        this.contact = contact;
        this.customer = customer;
        this.unknownContact = this.toUnknownContact(contact);
        this.isDummy = this.toDummy(contact);
        this.contactName = this.toContactName(contact, customer);
        this.contactCompanyName = this.toContactCompanyName(customer);
        this.contactCivility = this.toContactCivility(customer);
        this.contactSkills = this.toContactSkills(contact);
        this.contactTransferred = this.toContactTransferred(contact);
        this.contactTransferredByABot = this.toContactTransferredByABot(contact);
        const forwardMsg = this.toTransferredMessage(contact);
        try {
          this.transferredMessage = decodeURIComponent(escape(forwardMsg));
        } catch (_ignore) {
          this.transferredMessage = forwardMsg;
        }
        this.transferredAttachments = this.toTransferredAttachments(contact);
        this.transferRequestingCsrName = this.toTransferRequestingCsrName(contact);
        this.transferOldSkillNames = this.toTransferOldSkillNames(contact);
        this.contactRescheduled = this.toContactRescheduled(contact);
        this.isUnitOutgoingCallFakeContact = this.toUnitOutgoingCallFakeContact(contact);
        this.isUnitOutgoingMailFakeContact = this.toUnitOutgoingMailFakeContact(contact);
        this.isSocialNetworkFakeContact = this.toSocialNetworkFakeContact(contact);
        this.contactRescheduledDate = this.toContactRescheduledDate(contact);
        this.contactRescheduledDescription = this.toContactRescheduledDescription(contact);
      });
    // intialiaze presomption if no customer found
    this.contact$
      .pipe(
        isNotNullOrUndefined(),
        filter(({ customerId }) => !customerId && !this.isUnitOutgoingCallFakeContact),
        ng1Destroy(this)
      )
      .subscribe(contact => {
        this.customerCardsDeprecatedService.getCustomerCards(contact).then(data => {
          const [firstData] = data;
          if (data.length === 1) {
            this.contactFacade.link(contact.responseId, firstData.customer.id);
            this.contactName = this.toContactName(contact, firstData.customer);
          } else if (data.length > 1) {
            this.customers = data.map(({ customer }) => customer);
            this.selectCustomer = firstData.customer;
            this.contactName = `${this.$i18next.t('agent:contact.info.undetermined_contact')}`;
          }
        });
      });
  }

  readableName(customer: Customer): string {
    let customerCompanyName = '';
    if (customer.companyname) {
      customerCompanyName = ' - ' + capitalize(customer.companyname);
    }
    if (!!customer.lastname && !!customer.firstname) {
      return `${customer.firstname.toLowerCase()} ${customer.lastname.toLowerCase()} ${customerCompanyName}`;
    } else {
      return `${this.$i18next.t('agent:contact.info.contact')} ${this.$i18next.t('agent:contact.info.unvalued_contact')}`;
    }
  }

  formatRescheduleDate(rescheduleDate: Date): string {
    return this.momentService.readableDateTimeNoSep(rescheduleDate);
  }

  openCustomerCard(): void {
    if (this.unknownContact) {
      this.openNewCustomerCard();
    } else {
      this.openSpecificCustomerCard(this.contact?.customerId!);
    }
  }

  openNewCustomerCard(): void {
    const uri = this.contact?.phoneNumber ? this.contact?.phoneNumber : '';
    const channel = this.contact?.channel ? this.toNormalizeChannel(this.contact?.channel) : ChanEnum.NotSet;
    this.customerFacade.manageDialog({ uri, channel: ChannelUtil.toString(channel) });
  }

  openSpecificCustomerCard(customerId: string): void {
    this.customerFacade.manageDialog(customerId);
  }

  selectCustomerCard(): void {
    if (this.selectCustomer && this.contact) {
      this.contactFacade.link(this.contact.responseId, this.selectCustomer.id);
      this.hideHelpIcon = true;
    }
  }

  private toUnknownContact(contact: Booking | undefined) {
    return !(contact && contact.customerId);
  }

  private toDummy(contact: Booking | undefined): boolean {
    return !!contact && ChannelUtil.areSame(contact.channel, ChannelUtil.OUTGOING_MAIL) && contact.phoneNumber === 'UNIT_OUTGOING_MAIL';
  }

  private toContactName(contact: Booking | undefined, customer: Customer | undefined): string {
    return Option.of(contact)
      .map(_contact => {
        if ((this.toUnitOutgoingCallFakeContact(contact) || this.toUnitOutgoingMailFakeContact(contact)) && !_contact.customerId) {
          return this.$i18next.t('agent:contact.workspace.phone.unit_outgoing_call.title');
        }
        if (this.toSocialNetworkFakeContact(contact)) {
          return this.$i18next.t('agent:contact.workspace.phone.unit_outgoing_call.title');
        }
        if (this.toUnknownContact(contact) && !contact!.context) {
          return `${this.$i18next.t('agent:contact.info.unknown_contact')}`;
        }
        if (customer) {
          return UserUtil.fullname(customer) ?? this.$i18next.t(UserUtil.UNKNOWN);
        }
        if (this.customers.length > 0) {
          return `${this.$i18next.t('agent:contact.info.contact')} ${this.$i18next.t('agent:contact.info.undetermined_contact')}`;
        }

        if (_contact.customerId) {
          return (_contact.customerName && _contact.customerName.toLowerCase()) || '';
        }
        return _contact.context['lastName'].concat(' ').concat(_contact.context['firstName'].toLowerCase()) || '';
      })
      .getOrElse('');
  }

  private toContactCompanyName(customer: Customer | undefined): string {
    return (customer && customer.companyname) || '';
  }

  private toContactCivility(customer: Customer | undefined): Civility {
    return (customer && customer.civility) || Civility.NOTSET;
  }

  private toContactSkills(contact: Booking | undefined): string[] {
    return (contact && contact.skills) || [];
  }

  private toContactTransferred(contact: Booking | undefined): boolean {
    const transferred = (!!contact && !!contact.forward) || false;
    return transferred || this.toContactTransferredByABot(contact);
  }

  private toContactTransferredByABot(contact: Booking | undefined): boolean {
    return (contact && !!contact.lastBotName) || false;
  }

  private toTransferredMessage(contact: Booking | undefined): string {
    const comment = (contact && contact.forward && contact.forward.comment) || '';
    try {
      return JSON.parse(comment).comment;
    } catch (e) {
      return comment;
    }
  }

  private toTransferredAttachments(contact: Booking | undefined): IEpticaExctractAttachment[] {
    return (contact && contact.forward && contact.forward.attachments) || [];
  }

  private toTransferRequestingCsrName(contact: Booking | undefined): string {
    const agentName = (contact && contact.forward && contact.forward.agentName) || false;
    const lastBotName = (contact && contact.lastBotName) || false;
    return agentName || lastBotName || '';
  }

  private toTransferOldSkillNames(contact: Booking | undefined): string[] {
    return (contact && contact.forward && contact.forward.previousSkills) || [];
  }

  private toContactRescheduled(contact: Booking | undefined): boolean {
    return (contact && !!contact.campaign?.rescheduledDate) || false;
  }

  private toUnitOutgoingCallFakeContact(contact: Booking | undefined): boolean {
    return (contact && this.unitOutgoingCallDeprecatedService.isUnitOutgoingCallFakeContact(contact)) || false;
  }

  private toUnitOutgoingMailFakeContact(contact: Booking | undefined): boolean {
    return (contact && this.outgoingMailDeprecatedService.isFakeUnitMailKnowContact(contact.responseId)) || false;
  }

  private toSocialNetworkFakeContact(contact: Booking | undefined): boolean {
    const channel = (contact && contact.channel) || ChanEnum.NotSet;
    const phoneNumber = (contact && contact.phoneNumber) || '';
    return channel.type === ChannelType.SOCIAL_NETWORK && !!phoneNumber && phoneNumber.indexOf('SOCIAL_NETWORK_BASKET') !== -1;
  }

  private toContactRescheduledDate(contact: Booking | undefined): Date | undefined {
    const rescheduledDate = contact && contact.campaign?.rescheduledDate;
    return rescheduledDate ? new Date(rescheduledDate) : undefined;
  }

  private toContactRescheduledDescription(contact: Booking | undefined): string | undefined {
    return contact && contact.campaign?.rescheduledDescription;
  }

  private toNormalizeChannel(channel: Channel): Channel {
    if (ChannelUtil.equals(channel, ChannelUtil.INCOMING_BASKET)) {
      return ChannelUtil.INCOMING_MAIL;
    }
    return channel;
  }
}

export class ContactInfo implements IComponentOptions {
  templateUrl = templateUrl;
  controller = [
    '$i18next',
    'customerCardsDeprecatedService',
    'unitOutgoingCallDeprecatedService',
    'outgoingMailDeprecatedService',
    'momentService',
    'contactFacade',
    'customerFacade',
    ContactInfoCtrl,
  ];
}

export default MODULE_NAME;
